import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Skeleton, Tab } from "@mui/material";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import { Avatar, getAvatar } from "../../components/Avatar";
import { Config } from "../../config/config";
import { getGlobalVar, Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { DefaultProps } from "../LayoutWrapper";

interface State {
    avatarModalVisible: boolean;
    activeTab: string;
    loaded: boolean;
    inventory: any[];
}

interface Props extends DefaultProps {
    playerData: any;
    handlePlayerDataChange(name: any, value: any, type?: string): void;
}

const AVATARS = getGlobalVar("ITEMS").filter(item => item.type === "avatars" && !item.name.includes("disabled"));

const FRAMES = getGlobalVar("ITEMS").reduce((list, item) => {
    if (item.type === "avatar_frames") {
        try {
            list.push({
                ...item,
                image: require(`../../../assets/images/misc/not_found.png`).default,
            })
        } catch (error) {
            console.log(error);
        }
    }

    return list;
}, []);

class EditPlayerAvatarMain extends React.Component<Props, State> {

    constructor(props) {
        super(props);

        this.state = {
            avatarModalVisible: false,
            activeTab: "avatars",
            loaded: false,
            inventory: [],
        };
    }

    fetchInventory = () => axios.get(`${Config.getInstance.apiUrl}/api/user/${this.props.playerData.id}/inventory`, {
        headers: {Authorization: Helpers.getSessionId(), env: this.props.activeEnv},
    })

    fetchData = () =>
        axios.all([this.fetchInventory()]).then(axios.spread((
            inventory,
        ) => {
            this.setState({
                loaded: true,
                inventory: inventory.data.data,
            });
        })).catch((_err) => {
            PopupMessage.error("Something went wrong");
        })

    showAvatarDialog() {
        if (this.props?.playerData?.is_online) {
            return null;
        }

        this.setState({
            avatarModalVisible: true,
        }, () => {
            if (this.state.inventory.length === 0) {
                this.fetchData();
            }
        })
    }

    hideAvatarDialog() {
        this.setState({
            avatarModalVisible: false,
            activeTab: "avatars"
        })
    }

    changeTab(value) {
        this.setState({
            activeTab: value,
        })
    }

    isDisabled(itemId) {
        return !this.state.inventory.find(item => item.itemId === itemId);
    }

    unlockItem(itemId, type: string, unlock = false) {
        if (!unlock) {
            return;
        }

        axios.put(`${Config.getInstance.apiUrl}/api/user/${this.props.playerData.id}/inventory`, {
            amount: 1,
            id: itemId,
            type,
        },
        {
            headers: {
                Authorization: Helpers.getSessionId(),
            }
        })
        .then(_ => {
            const tempInventory = [...this.state.inventory];

            tempInventory.push({
                itemId,
                amount: 1,
                itemType: type
            });

            this.setState({
                inventory: tempInventory
            })
        })
        .catch(_ => {
            PopupMessage.error("Couldn't unlock item!");
        });
    }

    render() {
        if (this.props.playerData.is_online) {
            return Avatar(this.props.playerData);
        }

        return <React.Fragment>
            <div style={{ cursor: this.props?.playerData?.is_online ? "default" : "pointer" }} onClick={() => this.showAvatarDialog()}>
                {Avatar(this.props.playerData)}
            </div>

            <Dialog
                open={this.state.avatarModalVisible}
                onClose={() => this.hideAvatarDialog()}
                fullWidth={true}
                maxWidth="sm"
            >
                <DialogContent dividers>
                    <TabContext value={this.state.activeTab}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <TabList onChange={(_e, value) => this.changeTab(value)}>
                                <Tab label="Avatars" value="avatars"/>
                                <Tab label="Frames" value="frames"/>
                            </TabList>
                        </Box>
                        <TabPanel value="avatars">
                            <RadioGroup
                                row={true}
                                style={{ display: "grid", gridTemplateColumns: "auto auto auto" }}
                                value={this.props.playerData.avatar}
                                onChange={(e) => this.props.handlePlayerDataChange("avatar", e.target.value, "number")}
                            >
                                {AVATARS.map(avatar => {
                                    const disabled = this.isDisabled(avatar.id);

                                    return <FormControlLabel
                                        style={{ flexGrow: 1, width: "32%", marginBottom: 10 }}
                                        className={`avatars-list--item ${disabled && "disabled"}`}
                                        key={avatar.id}
                                        value={avatar.id}
                                        control={<Radio disabled={disabled}/>}
                                        onClick={() => this.unlockItem(avatar.id, "avatars", disabled)}
                                        label={this.state.loaded ? <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexDirection: "column",
                                            width: 120
                                        }}>
                                            {getAvatar({ avatar: avatar.id })}
                                            <span style={{ margin: "10px 0", textAlign: "center", fontSize: 12 }}>{avatar.name}</span>
                                        </div> : <Skeleton width={120} height={120}/>}/>
                                })}
                            </RadioGroup>
                        </TabPanel>
                        <TabPanel value="frames">
                            <RadioGroup
                                row={true}
                                style={{ display: "grid", gridTemplateColumns: "auto auto auto" }}
                                value={this.props.playerData.avatar_frame}
                                onChange={(e) => this.props.handlePlayerDataChange("avatar_frame", e.target.value, "number")}
                            >
                                {FRAMES.map(frame => {
                                    const disabled = this.isDisabled(frame.id);

                                    return <FormControlLabel
                                        style={{ flexGrow: 1, width: "32%", marginBottom: 10 }}
                                        className={`avatars-list--item ${disabled && "disabled"}`}
                                        key={frame.id}
                                        value={frame.id}
                                        control={<Radio disabled={disabled}/>}
                                        onClick={() => this.unlockItem(frame.id, "avatar_frames", disabled)}
                                        label={this.state.loaded ? <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexDirection: "column",
                                            width: 120
                                        }}>
                                            <img src={frame.image} style={{ width: 100 }}/>
                                            <span style={{ margin: "10px 0", textAlign: "center", fontSize: 12 }}>{frame.name}</span>
                                        </div> : <Skeleton width={120} height={120}/>}/>
                                })}
                            </RadioGroup>
                        </TabPanel>
                    </TabContext>
                </DialogContent>

                <DialogActions>
                    <Button disableElevation onClick={() => this.hideAvatarDialog()} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    }
}

export const EditPlayerAvatar = createForm()(EditPlayerAvatarMain);
